import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { CategoryService } from '../../lib/api'
import { DefaultGetParamsProps } from '../../lib/api/types/interfaces'
import {
	GetCategoryDataResponse,
	GetPrimarySectorsDataResponse,
	UpdateCategoryDataProps,
} from '../../lib/api/types/category.interfaces'
import { handleApiError } from '../../lib/utils/handleApiError'
import toastCaller from '../../lib/utils/toastCaller'

export const useCategory = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetCategoryDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetCategoryDataResponse>({
		queryKey: ['items'],
		queryFn: () => CategoryService.get({ id }),
		enabled: isAuthenticated && Boolean(id),
		staleTime: 1000 * 5,
		retry: 1,
		...options,
	})
}

export const useCreateCategory = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['categoryCreate'],
		mutationFn: ({ data }: { data: UpdateCategoryDataProps }) =>
			CategoryService.create({ data }),
		onError: (error, { data }) => handleApiError(error, data?.name),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['organizationCategories'],
				exact: false,
			})
			toastCaller({
				message: 'Category was created!',
				type: 'success',
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useUpdateCategory = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['categoryUpdate'],
		mutationFn: ({
			data,
			id,
			toastMessage,
		}: {
			data: UpdateCategoryDataProps
			id: string
			toastMessage?: string
		}) => CategoryService.update({ data, id }),
		onError: (error, { data }) => handleApiError(error, data?.name),
		onSuccess: (_, { toastMessage }) => {
			queryClient.invalidateQueries({
				queryKey: ['organizationCategories'],
				exact: false,
			})
			toastCaller({
				message: toastMessage || 'Category was updated!',
				type: 'success',
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteCategory = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['categoryDelete'],
		mutationFn: ({ id }: DefaultGetParamsProps) =>
			CategoryService.delete({ id }),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['organizationCategories'],
				exact: false,
			})
			toastCaller({
				message: 'Category was deleted!',
				type: 'success',
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useGenerateCategoryPrompt = () => {
	const mutation = useMutation({
		mutationKey: ['categoryGenerateCategoryPrompt'],
		mutationFn: ({ text }: { text: string }) =>
			CategoryService.promptGenerate({ text }),
		onError: (error) => handleApiError(error),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useGetPrimarySectors = (
	options?: Partial<QueryObserverOptions<GetPrimarySectorsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetPrimarySectorsDataResponse>({
		queryKey: ['primarySectors'],
		queryFn: () => CategoryService.getPrimarySectors(),
		enabled: isAuthenticated,
		staleTime: 1000 * 5,
		retry: 1,
		...options,
	})
}
